import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMindmap } from '../contexts/MindmapContext';
import { Plus, Search, Trash2, Brain, Upload, Clock, ChevronDown, HardDrive, Cloud } from 'lucide-react';
import { localMindmapService } from '../services/localMindmapService';

export default function HomePage() {
  const navigate = useNavigate();
  const { mindmaps, loading, loadMindmaps, createMindmap, deleteMindmap, importMindmap } = useMindmap();
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const [localMindmaps, setLocalMindmaps] = useState([]);

  useEffect(() => {
    loadMindmaps();
    setLocalMindmaps(localMindmapService.listLocalMindmaps());
  }, [loadMindmaps]);

  const refreshLocalMindmaps = () => {
    setLocalMindmaps(localMindmapService.listLocalMindmaps());
  };

  const handleCreateMindmap = async (storage = 'cloud') => {
    setShowCreateMenu(false);

    if (storage === 'local') {
      const localMap = localMindmapService.createLocalMindmap('Untitled Mindmap');
      refreshLocalMindmaps();
      navigate(`/mindmap/${localMap.id}`);
      return;
    }

    const cloudMap = await createMindmap('Untitled Mindmap');
    if (cloudMap) {
      localMindmapService.setSavePreference(cloudMap.id, 'cloud');
      navigate(`/mindmap/${cloudMap.id}`);
    }
  };

  const handleDelete = async (mindmap) => {
    if (!mindmap) return;

    if (mindmap.storage_type === 'local') {
      localMindmapService.deleteLocalMindmap(mindmap.id);
      refreshLocalMindmaps();
    } else {
      await deleteMindmap(mindmap.id);
    }

    setShowDeleteConfirm(null);
  };

  const handleSaveLocationChange = (mindmapId, nextLocation) => {
    localMindmapService.setSavePreference(mindmapId, nextLocation);
    refreshLocalMindmaps();
  };

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      if (!data.mindmap || !data.nodes) {
        alert('Invalid mindmap file format');
        return;
      }

      const mindmap = await importMindmap(data);
      if (mindmap) {
        navigate(`/mindmap/${mindmap.id}`);
      }
    } catch (error) {
      console.error('Import error:', error);
      alert('Failed to import mindmap');
    }

    event.target.value = '';
  };

  const cloudMindmaps = mindmaps.map((mindmap) => ({
    ...mindmap,
    storage_type: 'cloud',
    default_save_location: localMindmapService.getSavePreference(mindmap.id, 'cloud'),
  }));

  const mergedMindmaps = [...cloudMindmaps, ...localMindmaps]
    .sort((a, b) => (b.updated_at || 0) - (a.updated_at || 0));

  const filteredMindmaps = mergedMindmaps.filter((mindmap) =>
    (mindmap.title || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Mindmaps</h1>
          <p className="text-gray-500 mt-1">Create and organize your ideas</p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <label className="flex-1 sm:flex-none">
            <input
              type="file"
              accept=".json"
              onChange={handleImport}
              className="hidden"
            />
            <div className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
              <Upload className="w-4 h-4" />
              <span className="text-sm">Import</span>
            </div>
          </label>
          <div className="relative flex-1 sm:flex-none">
            <button
              onClick={() => setShowCreateMenu((open) => !open)}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span className="text-sm">New Mindmap</span>
              <ChevronDown className="w-4 h-4" />
            </button>

            {showCreateMenu && (
              <div className="absolute right-0 mt-1 w-52 bg-white border rounded-lg shadow-lg z-20 overflow-hidden">
                <button
                  onClick={() => handleCreateMindmap('cloud')}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 text-gray-700 flex items-center gap-2"
                >
                  <Cloud className="w-4 h-4" />
                  New Cloud Mindmap
                </button>
                <button
                  onClick={() => handleCreateMindmap('local')}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 text-gray-700 flex items-center gap-2"
                >
                  <HardDrive className="w-4 h-4" />
                  New Local-only Mindmap
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search mindmaps..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        />
      </div>

      {/* Mindmaps grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white rounded-xl p-6 h-40 skeleton" />
          ))}
        </div>
      ) : filteredMindmaps.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl">
          <Brain className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchQuery ? 'No mindmaps found' : 'No mindmaps yet'}
          </h3>
          <p className="text-gray-500 mb-4">
            {searchQuery 
              ? 'Try a different search term' 
              : 'Create your first mindmap to get started'}
          </p>
          {!searchQuery && (
            <button
              onClick={() => handleCreateMindmap('cloud')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
            >
              <Plus className="w-4 h-4" />
              Create Mindmap
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredMindmaps.map(mindmap => (
            <div
              key={mindmap.id}
              className="bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow cursor-pointer group"
              onClick={() => navigate(`/mindmap/${mindmap.id}`)}
            >
              <div className="flex items-start justify-between mb-3 gap-2">
                <div className="flex items-start gap-2">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Brain className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex flex-col gap-1">
                    {mindmap.storage_type === 'local' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-700">
                        <HardDrive className="w-3 h-3" />
                        Local
                      </span>
                    ) : (
                      <div
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1"
                      >
                        <button
                          type="button"
                          onClick={() => handleSaveLocationChange(mindmap.id, 'cloud')}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] border transition-colors ${mindmap.default_save_location === 'cloud' ? 'bg-blue-100 text-blue-700 border-blue-200' : 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200'}`}
                          title="Default save: Cloud"
                          aria-label="Default save: Cloud"
                        >
                          <Cloud className="w-3 h-3" />
                          Cloud
                        </button>
                        <button
                          type="button"
                          onClick={() => handleSaveLocationChange(mindmap.id, 'local')}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] border transition-colors ${mindmap.default_save_location === 'local' ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200'}`}
                          title="Default save: Local"
                          aria-label="Default save: Local"
                        >
                          <HardDrive className="w-3 h-3" />
                          Local
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDeleteConfirm(mindmap);
                  }}
                  className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <h3 className="font-semibold text-gray-900 mb-1 truncate">
                {mindmap.title || 'Untitled Mindmap'}
              </h3>
              <div className="flex items-center gap-1 text-sm text-gray-500">
                <Clock className="w-4 h-4" />
                {formatDate(mindmap.updated_at)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Mindmap?</h3>
            <p className="text-gray-500 mb-4">
              {showDeleteConfirm.storage_type === 'local'
                ? 'This local-only mindmap will be removed from this browser.'
                : 'This action cannot be undone. The mindmap will be permanently deleted from cloud.'}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(showDeleteConfirm)}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
